
import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Image as ImageIcon, 
  Camera, 
  History, 
  Info, 
  Trash2, 
  Search, 
  ChevronRight, 
  X, 
  Check, 
  Copy,
  Loader2,
  Sparkles,
  Layers,
  ExternalLink,
  RefreshCw,
  Circle
} from 'lucide-react';
import { recognizeBusinessCards, searchCardInfo } from './geminiService';
import { RecognitionRecord, BusinessCardData } from './types';
import { compressImage, copyToClipboard } from './utils';

// --- Reusable Kawaii Components ---

const KawaiiButton: React.FC<{
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}> = ({ onClick, variant = 'primary', children, className = '', disabled }) => {
  const baseStyles = "px-6 py-3 rounded-full font-bold transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-pink-400 text-white hover:bg-pink-500 shadow-lg shadow-pink-200",
    secondary: "bg-purple-100 text-purple-600 hover:bg-purple-200",
    danger: "bg-red-100 text-red-500 hover:bg-red-200",
    ghost: "bg-white/50 text-slate-600 hover:bg-white",
  };
  
  return (
    <button 
      onClick={onClick} 
      className={`${baseStyles} ${variants[variant]} ${className}`}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] p-8 shadow-2xl relative animate-in zoom-in-95 duration-300">
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 transition-colors"
        >
          <X className="w-6 h-6 text-slate-400" />
        </button>
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <Sparkles className="text-pink-400 w-6 h-6" /> {title}
        </h2>
        {children}
      </div>
    </div>
  );
};

// --- Camera View Component ---

const CameraOverlay: React.FC<{
  onCapture: (base64: string) => void;
  onClose: () => void;
}> = ({ onCapture, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });
      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        videoRef.current.onloadedmetadata = () => {
          setIsReady(true);
        };
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("無法開啟相機，請檢查權限設定 (｡•́︿•̀｡)");
      onClose();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const capture = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const base64 = canvas.toDataURL('image/jpeg', 0.8);
      onCapture(base64);
    }
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col">
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        {/* Card Alignment Helper Frame */}
        <div className="relative z-10 w-4/5 aspect-[1.586/1] border-2 border-white/50 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] pointer-events-none">
          <div className="absolute -top-6 left-0 right-0 text-center">
            <span className="bg-black/50 text-white text-[10px] font-bold px-3 py-1 rounded-full backdrop-blur-sm">
              請將名片置於框內
            </span>
          </div>
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-pink-400 -mt-1 -ml-1 rounded-tl-lg"></div>
          <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-pink-400 -mt-1 -mr-1 rounded-tr-lg"></div>
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-pink-400 -mb-1 -ml-1 rounded-bl-lg"></div>
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-pink-400 -mb-1 -mr-1 rounded-br-lg"></div>
        </div>

        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-start z-20">
          <button 
            onClick={onClose}
            className="w-10 h-10 bg-black/40 text-white rounded-full flex items-center justify-center backdrop-blur-md hover:bg-black/60 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <button 
            onClick={toggleCamera}
            className="w-10 h-10 bg-black/40 text-white rounded-full flex items-center justify-center backdrop-blur-md hover:bg-black/60 transition-colors"
          >
            <RefreshCw className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Bottom Shutter Area */}
      <div className="h-40 bg-black flex items-center justify-center gap-12 px-6">
        <div className="w-10" /> {/* Placeholder for balance */}
        <button 
          onClick={capture}
          disabled={!isReady}
          className="relative group disabled:opacity-50"
        >
          <div className="absolute inset-0 bg-white/20 rounded-full scale-125 group-active:scale-150 transition-transform"></div>
          <div className="relative w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl">
            <div className="w-16 h-16 border-4 border-slate-900 rounded-full"></div>
          </div>
        </button>
        <div className="w-10" />
      </div>
    </div>
  );
};

// --- Main App ---

const App: React.FC = () => {
  const [records, setRecords] = useState<RecognitionRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<{ content: string; name: string } | null>(null);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load from LocalStorage
  useEffect(() => {
    const saved = localStorage.getItem('sting_cards_v2');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setRecords(parsed);
        if (parsed.length > 0 && !selectedId) setSelectedId(parsed[0].id);
      } catch (e) {
        console.error("Corrupted storage", e);
      }
    }
  }, []);

  const saveRecords = (newRecords: RecognitionRecord[]) => {
    setRecords(newRecords);
    localStorage.setItem('sting_cards_v2', JSON.stringify(newRecords));
  };

  const processRecognition = async (base64: string) => {
    setIsRecognizing(true);
    try {
      const compressed = await compressImage(base64);
      const cards = await recognizeBusinessCards(compressed);
      
      if (cards && cards.length > 0) {
        const newRecord: RecognitionRecord = {
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          originalImage: compressed,
          cards: cards,
        };
        const updated = [newRecord, ...records];
        saveRecords(updated);
        setSelectedId(newRecord.id);
        setIsUploadOpen(false);
        setIsCameraOpen(false);
      } else {
        alert("沒發現名片哦！請換一張試試看，或者確保光線充足喔 (๑•́ ₃ •̀๑)");
      }
    } catch (err) {
      alert("辨識過程發生錯誤，請稍後再試。");
    } finally {
      setIsRecognizing(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const rawBase64 = event.target?.result as string;
      await processRecognition(rawBase64);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const deleteRecord = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("確定要刪除這筆紀錄嗎？(｡•́︿•̀｡)")) {
      const updated = records.filter(r => r.id !== id);
      saveRecords(updated);
      if (selectedId === id) setSelectedId(updated[0]?.id || null);
    }
  };

  const handleSearch = async (card: BusinessCardData, recordId: string) => {
    const record = records.find(r => r.id === recordId);
    if (!record) return;

    const targetCard = record.cards.find(c => c.id === card.id);
    if (targetCard?.searchCache) {
      setSearchResult({ content: targetCard.searchCache, name: card.name });
      return;
    }

    setIsSearching(true);
    try {
      const result = await searchCardInfo(card.name, card.company);
      setSearchResult({ content: result.text, name: card.name });

      const updatedRecords = records.map(r => {
        if (r.id === recordId) {
          return {
            ...r,
            cards: r.cards.map(c => c.id === card.id ? { ...c, searchCache: result.text } : c)
          };
        }
        return r;
      });
      saveRecords(updatedRecords);
    } catch (error) {
      alert("搜尋出了點小問題，晚點再試試吧 (╥﹏╥)");
    } finally {
      setIsSearching(false);
    }
  };

  const handleCopyResult = async () => {
    if (searchResult) {
      await copyToClipboard(searchResult.content);
      setIsCopying(true);
      setTimeout(() => setIsCopying(false), 2000);
    }
  };

  const currentRecord = records.find(r => r.id === selectedId);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="h-20 bg-white shadow-sm flex items-center justify-between px-4 md:px-10 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-pink-400 rounded-2xl flex items-center justify-center text-white rotate-3 hover:rotate-0 transition-transform cursor-pointer shadow-lg shadow-pink-100">
            <Sparkles className="w-8 h-8" />
          </div>
          <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight">
            Sting <span className="text-pink-400">名片紀錄</span>
          </h1>
        </div>
        
        <KawaiiButton onClick={() => setIsUploadOpen(true)}>
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">新增掃描</span>
        </KawaiiButton>
      </nav>

      <main className="flex-1 flex flex-col md:flex-row overflow-hidden h-[calc(100vh-5rem)]">
        {/* Left Sidebar: History */}
        <aside className={`
          w-full md:w-80 lg:w-96 bg-white border-r border-slate-100 flex flex-col transition-all duration-300
          ${selectedId && 'hidden md:flex'}
        `}>
          <div className="p-6 border-b border-slate-50">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <History className="w-4 h-4" /> 歷史紀錄
            </h3>
          </div>
          
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
            {records.length === 0 ? (
              <div className="text-center py-20 px-6">
                <div className="w-24 h-24 bg-pink-50 rounded-full mx-auto mb-4 flex items-center justify-center animate-bounce-subtle">
                  <ImageIcon className="w-10 h-10 text-pink-200" />
                </div>
                <p className="text-slate-400 text-sm font-medium">還沒有任何紀錄喔！<br/>快點擊右上角「新增掃描」吧</p>
              </div>
            ) : (
              records.map(record => (
                <div 
                  key={record.id}
                  onClick={() => setSelectedId(record.id)}
                  className={`
                    group p-4 rounded-[1.5rem] cursor-pointer transition-all duration-200 border-2 relative
                    ${selectedId === record.id 
                      ? 'bg-pink-50 border-pink-100 shadow-md translate-x-1' 
                      : 'bg-white border-transparent hover:bg-slate-50'}
                  `}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-slate-100 flex-shrink-0 relative">
                      <img src={record.originalImage} className="w-full h-full object-cover" alt="Thumb" />
                      {record.cards.length > 1 && (
                        <div className="absolute top-1 right-1 bg-pink-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                          {record.cards.length}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-700 truncate">
                        {record.cards[0]?.name || "未命名"}
                        {record.cards.length > 1 && <span className="text-pink-400 ml-1">等 {record.cards.length} 位</span>}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(record.timestamp).toLocaleString('zh-TW', { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                    </div>
                    <button 
                      onClick={(e) => deleteRecord(record.id, e)}
                      className="p-2 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Right Content: Details */}
        <section className={`
          flex-1 bg-slate-50 overflow-y-auto custom-scrollbar
          ${!selectedId && 'hidden md:flex flex-col items-center justify-center'}
        `}>
          {!currentRecord ? (
            <div className="text-center max-w-sm px-6">
              <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl kawaii-shadow">
                <Search className="w-12 h-12 text-pink-300" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">請選擇一項紀錄</h2>
              <p className="text-slate-500">從左側列表點擊你想要查看的名片資料 (๑•᎑•๑)</p>
            </div>
          ) : (
            <div className="p-6 md:p-12 max-w-5xl mx-auto space-y-8">
              {/* Mobile Back Button */}
              <button 
                onClick={() => setSelectedId(null)}
                className="md:hidden flex items-center gap-2 text-pink-500 font-bold mb-4"
              >
                <ChevronRight className="w-5 h-5 rotate-180" /> 返回列表
              </button>

              <div className="flex flex-col lg:flex-row gap-8 items-start">
                {/* Image Section */}
                <div className="w-full lg:w-1/3 space-y-4 lg:sticky lg:top-0">
                  <div className="flex justify-between items-end">
                    <h4 className="text-slate-400 font-black text-[10px] uppercase tracking-widest">原始照片</h4>
                    {currentRecord.cards.length > 1 && (
                      <span className="bg-pink-100 text-pink-500 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Layers className="w-3 h-3" /> 偵測到 {currentRecord.cards.length} 張名片
                      </span>
                    )}
                  </div>
                  <div className="bg-white p-4 rounded-[2rem] shadow-lg kawaii-shadow border border-white/50 overflow-hidden">
                    <img 
                      src={currentRecord.originalImage} 
                      className="w-full h-auto rounded-2xl hover:scale-110 transition-transform duration-500 cursor-zoom-in" 
                      alt="Full scan result" 
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 text-center italic">提示：如果是多張名片，AI 會逐一解析下方內容</p>
                </div>

                {/* Cards Section */}
                <div className="w-full lg:w-2/3 space-y-6">
                  <h4 className="text-slate-400 font-black text-[10px] uppercase tracking-widest">
                    辨識結果清單
                  </h4>
                  {currentRecord.cards.map((card, idx) => (
                    <div 
                      key={card.id} 
                      className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-lg kawaii-shadow border border-slate-50 relative overflow-hidden group"
                    >
                      {currentRecord.cards.length > 1 && (
                        <div className="absolute top-0 right-0 bg-slate-50 text-slate-300 text-4xl font-black px-4 py-2 select-none group-hover:text-pink-50 transition-colors">
                          0{idx + 1}
                        </div>
                      )}

                      <div className="relative z-10">
                        <div className="flex justify-between items-start mb-6">
                          <div>
                            <h2 className="text-3xl font-black text-slate-800 tracking-tight">{card.name}</h2>
                            <p className="text-pink-500 font-bold mt-1 text-lg">{card.title || "職務未標註"}</p>
                          </div>
                          <button 
                            onClick={() => handleSearch(card, currentRecord.id)}
                            className="w-12 h-12 bg-sky-50 text-sky-500 rounded-2xl flex items-center justify-center hover:bg-sky-100 hover:scale-110 transition-all shadow-sm"
                            title="AI 背景搜尋"
                          >
                            <Search className="w-6 h-6" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
                          {card.company && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-400 flex items-center justify-center flex-shrink-0 mt-0.5"><Plus className="w-4 h-4 rotate-45"/></div>
                              <div>
                                <p className="text-[10px] font-black text-slate-300 uppercase">公司</p>
                                <p className="font-bold text-slate-700">{card.company}</p>
                              </div>
                            </div>
                          )}
                          {card.phone && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5"><Camera className="w-4 h-4"/></div>
                              <div>
                                <p className="text-[10px] font-black text-slate-300 uppercase">電話</p>
                                <p className="font-bold text-slate-700 tracking-wide">{card.phone}</p>
                              </div>
                            </div>
                          )}
                          {card.email && (
                            <div className="flex items-start gap-3 overflow-hidden">
                              <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-400 flex items-center justify-center flex-shrink-0 mt-0.5"><ImageIcon className="w-4 h-4"/></div>
                              <div className="min-w-0">
                                <p className="text-[10px] font-black text-slate-300 uppercase">電子信箱</p>
                                <p className="font-bold text-slate-700 truncate">{card.email}</p>
                              </div>
                            </div>
                          )}
                          {card.website && (
                            <div className="flex items-start gap-3 overflow-hidden">
                              <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-400 flex items-center justify-center flex-shrink-0 mt-0.5"><ExternalLink className="w-4 h-4"/></div>
                              <div className="min-w-0">
                                <p className="text-[10px] font-black text-slate-300 uppercase">網站</p>
                                <p className="font-bold text-slate-700 truncate underline underline-offset-4 decoration-purple-200">{card.website}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {card.address && (
                          <div className="mt-6 pt-6 border-t border-slate-50 flex items-start gap-3">
                             <div className="w-8 h-8 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center flex-shrink-0 mt-0.5"><History className="w-4 h-4"/></div>
                             <div>
                                <p className="text-[10px] font-black text-slate-300 uppercase">地址</p>
                                <p className="text-sm font-medium text-slate-600">{card.address}</p>
                             </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Upload Modal */}
      <Modal 
        isOpen={isUploadOpen} 
        onClose={() => setIsUploadOpen(false)} 
        title="來點新名片吧！"
      >
        <div className="space-y-6">
          <p className="text-slate-500 font-medium text-center">支援單張或多張名片同時辨識喔！📸</p>
          
          <div className="grid grid-cols-2 gap-4">
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-4 p-8 rounded-[2.5rem] border-2 border-dashed border-pink-100 hover:border-pink-300 hover:bg-pink-50 transition-all group"
            >
              <div className="w-16 h-16 bg-pink-100 rounded-3xl flex items-center justify-center text-pink-500 group-hover:scale-110 transition-transform shadow-sm">
                <ImageIcon className="w-8 h-8" />
              </div>
              <span className="font-bold text-slate-700">上傳圖片</span>
            </button>

            <button 
              onClick={() => {
                setIsUploadOpen(false);
                setIsCameraOpen(true);
              }}
              className="flex flex-col items-center justify-center gap-4 p-8 rounded-[2.5rem] border-2 border-dashed border-purple-100 hover:border-purple-300 hover:bg-purple-50 transition-all group"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-3xl flex items-center justify-center text-purple-500 group-hover:scale-110 transition-transform shadow-sm">
                <Camera className="w-8 h-8" />
              </div>
              <span className="font-bold text-slate-700">開啟相機</span>
            </button>
          </div>

          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept="image/*" 
            onChange={handleFileUpload}
          />
          <p className="text-[10px] text-slate-400 text-center italic">提示：將多張名片平放拍照，AI 會幫你一次整理好！</p>
        </div>
      </Modal>

      {/* Camera Interface */}
      {isCameraOpen && (
        <CameraOverlay 
          onCapture={(base64) => {
            setIsCameraOpen(false);
            processRecognition(base64);
          }}
          onClose={() => setIsCameraOpen(false)}
        />
      )}

      {/* Recognizing State Overlay */}
      {isRecognizing && (
        <div className="fixed inset-0 z-[80] bg-white/95 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500">
          <div className="w-48 h-48 relative">
            <div className="absolute inset-0 bg-pink-200 rounded-full animate-ping opacity-20"></div>
            <div className="absolute inset-4 bg-pink-100 rounded-full animate-pulse"></div>
            <div className="relative z-10 w-full h-full flex items-center justify-center">
              <Sparkles className="w-24 h-24 text-pink-400 animate-bounce-subtle" />
            </div>
          </div>
          <h2 className="text-3xl font-black text-slate-800 mt-12 mb-4 tracking-tight">AI 掃描中...</h2>
          <p className="text-slate-500 max-w-xs leading-relaxed font-bold">正在逐一辨識影像中的所有名片，請稍等喔 (⁎⁍̴̛ᴗ⁍̴̛⁎)</p>
          <div className="mt-8 flex gap-2">
            <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-2 h-2 bg-pink-300 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-2 h-2 bg-pink-200 rounded-full animate-bounce"></div>
          </div>
        </div>
      )}

      {/* Search Result Modal */}
      <Modal 
        isOpen={!!searchResult || isSearching} 
        onClose={() => {
          setSearchResult(null);
          setIsSearching(false);
        }} 
        title={isSearching ? "正在搜尋..." : `關於 ${searchResult?.name}`}
      >
        <div className="min-h-[300px]">
          {isSearching ? (
            <div className="flex flex-col items-center justify-center h-64 gap-6">
              <div className="relative">
                <Loader2 className="w-16 h-16 text-sky-400 animate-spin" />
                <Search className="absolute inset-0 m-auto w-6 h-6 text-sky-300" />
              </div>
              <p className="text-slate-500 font-black">正在幫您打聽情報中...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-slate-50 p-6 rounded-[2rem] max-h-[400px] overflow-y-auto custom-scrollbar border border-slate-100">
                <div className="prose prose-slate prose-pink max-w-none">
                  {searchResult?.content.split('\n').map((line, i) => (
                    <p key={i} className="mb-3 text-slate-700 leading-relaxed font-medium">
                      {line || <br />}
                    </p>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <KawaiiButton onClick={handleCopyResult} className="flex-1" variant={isCopying ? "secondary" : "primary"}>
                  {isCopying ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  {isCopying ? "已複製！" : "複製內容"}
                </KawaiiButton>
                <KawaiiButton 
                  onClick={() => setSearchResult(null)} 
                  variant="ghost" 
                  className="px-4"
                >
                  關閉
                </KawaiiButton>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default App;
